import React, { ChangeEvent } from 'react';

// Redeclare forwardRef
declare module 'react' {
	function forwardRef<T, P = Record<string, never>>(
		render: (props: P, ref: React.Ref<T>) => React.ReactNode | null,
	): (props: P & React.RefAttributes<T>) => React.ReactNode | null;
}

import { ValueLabel } from '@/types';
import styles from './SelectDropdown.module.scss';

type SorN = string | number;

type SelectDropdownItem<V extends SorN, L extends SorN> = ValueLabel<V, L> & {
	link?: string;
	title?: string;
	rbf?: string;
};

type SelectDropdownProps<V extends SorN, L extends SorN> = {
	id?: string;
	name?: string;
	selected?: SelectDropdownItem<V, L>;
	options: SelectDropdownItem<V, L>[];
	favorites?: SelectDropdownItem<V, L>[];
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	defaultValue?: SelectDropdownItem<V, L>;
	rbf?: string;
	required?: boolean;
	onChange?: (selected: SelectDropdownItem<V, L>) => void;
};

const InnerComponent = <V extends SorN, L extends SorN>(
	props: SelectDropdownProps<V, L>,
	ref: React.ForwardedRef<HTMLSelectElement>,
): React.JSX.Element => {
	const {
		id,
		name,
		selected,
		options = [],
		favorites,
		placeholder,
		disabled = false,
		onChange,
		className,
		defaultValue,
		rbf,
		required = false,
	} = props;

	const dropdownStyle = [styles.container, disabled && styles.disabled, options.length === 1 && styles['one-choice'], className]
		.filter(Boolean)
		.join(' ');

	const handleChange = (evt: ChangeEvent<HTMLSelectElement>) => {
		if (onChange) {
			const searchValue = (x: SelectDropdownItem<V, L>) => x.value.toString() === evt.target.value;
			onChange(favorites?.find(searchValue) || options.find(searchValue));
		}
	};

	const generateOptions = (list: SelectDropdownItem<V, L>[]) =>
		list.map((o, i) => (
			<option id={id && `${id}-${o.value}`} key={`${o.value}-${i}`} value={o.value}>
				{o.label}
			</option>
		));

	return options.length > 0 ? (
		<div className={dropdownStyle} data-test-id={`dropdown${id ? '-' + id : ''}`}>
			<select
				ref={ref}
				aria-label="dropdown"
				id={id}
				data-test-id={`dropdown-select${id ? '-' + id : ''}`}
				name={name}
				value={selected?.value || ''}
				onChange={handleChange}
				disabled={disabled}
				required={required}
				data-rbf={rbf}
				defaultValue={defaultValue as any}
			>
				{placeholder && (
					<option value="" disabled hidden>
						{placeholder}
					</option>
				)}
				{favorites && favorites.length > 0 ? (
					<>
						<optgroup>{generateOptions(favorites)}</optgroup>
						<optgroup>{generateOptions(options)}</optgroup>
					</>
				) : (
					generateOptions(options)
				)}
			</select>
		</div>
	) : (
		<></>
	);
};

InnerComponent.displayName = 'SelectDropdown';

// TODO-TYPESCRIPT : trouver un moyen d'enlever ce any
const SelectDropdown = React.forwardRef(InnerComponent) as any;
export default SelectDropdown;
